import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, TrendingUp, BarChart3, Users, Target, ArrowRight } from 'lucide-react';
import { ContactForm } from './ContactForm';

const ForSuppliers: React.FC = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);

  const benefits = [
    {
      icon: Eye,
      title: "Visibility into how clinicians assess and select substitute products",
      description: "Gain insights into clinical decision-making processes and understand what drives product selection in healthcare settings."
    },
    {
      icon: TrendingUp,
      title: "Get listed as a preferred alternative in hospital workflows",
      description: "Position your products as trusted alternatives in our AI-powered recommendation engine."
    },
    {
      icon: BarChart3,
      title: "Access real-time insights and market intelligence dashboards",
      description: "Monitor market trends, competitor activity, and demand patterns with comprehensive analytics."
    }
  ];

  const features = [
    {
      icon: Target,
      title: "Market Intelligence",
      description: "Real-time data on product demand, shortages, and substitution patterns"
    },
    {
      icon: Users,
      title: "Clinical Insights",
      description: "Understanding of how clinicians evaluate and choose medical devices"
    },
    {
      icon: TrendingUp,
      title: "Growth Opportunities",
      description: "Identify new market opportunities and optimize product positioning"
    }
  ];

  return (
    <section id="for-suppliers" className="py-16 sm:py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12 sm:mb-16 lg:mb-24"
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-neutral-900 mb-4 sm:mb-6 px-2">
            For Suppliers
          </h2>
          <p className="text-lg sm:text-xl text-neutral-600 max-w-3xl mx-auto px-4">
            Connect with healthcare systems and gain valuable insights into clinical decision-making processes.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16 lg:mb-24">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="card p-6 sm:p-8"
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-secondary-100 rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <benefit.icon size={24} className="sm:w-8 sm:h-8 text-secondary-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-neutral-900 mb-3 sm:mb-4">
                {benefit.title}
              </h3>
              <p className="text-sm sm:text-base text-neutral-600 leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Features Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-br from-secondary-50 to-primary-50 rounded-3xl p-6 sm:p-8 lg:p-12 mb-12 sm:mb-16 lg:mb-24"
        >
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-neutral-900 mb-3 sm:mb-4">
              Comprehensive Supplier Platform
            </h3>
            <p className="text-base sm:text-lg text-neutral-600 max-w-2xl mx-auto px-4">
              Access powerful tools and insights to optimize your market presence and connect with healthcare systems.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-sm">
                  <feature.icon size={24} className="sm:w-8 sm:h-8 text-secondary-600" />
                </div>
                <h4 className="font-semibold text-neutral-900 mb-2 text-base sm:text-lg">
                  {feature.title}
                </h4>
                <p className="text-sm sm:text-base text-neutral-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Mission Statement */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-primary-400 to-secondary-400 rounded-3xl p-6 sm:p-8 lg:p-12 text-white">
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6">
              Bridge clinical decision-making with supply chain workflows
            </h3>
            <p className="text-lg sm:text-xl opacity-90 mb-6 sm:mb-8 max-w-3xl mx-auto">
              So no patient is delayed by a missing supply.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsContactFormOpen(true)}
              className="bg-white text-primary-600 font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-full hover:bg-gray-50 transition-colors text-base sm:text-lg touch-manipulation"
            >
              Partner with MedAlt
              <ArrowRight size={18} className="sm:w-5 sm:h-5 ml-2 inline" />
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Contact Form Modal */}
      <ContactForm
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
        title="Partner with MedAlt"
        buttonText="Submit Partnership Request"
      />
    </section>
  );
};

export { ForSuppliers }; 